import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Outlet } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { A11yProvider } from "./context/A11yContext";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import A11yPanel from "./components/layout/A11yPanel";
import { RequireAuth, Loading } from "./components/kit";
import { api, errMsg } from "./lib/api";
import Home from "./pages/Home";
import * as S from "./pages/StaticPages";
import * as A from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import { FreelanceList, ProjectDetail } from "./pages/Freelance";
import { GovList, GovDetail } from "./pages/Gov";
import { CommunityFeed, PostDetail } from "./pages/Community";
import { Directory, ProfessionalDetail } from "./pages/Directory";
import Dashboard from "./pages/Dashboard";
import * as M from "./pages/MyStuff";
import Notifications from "./pages/Notifications";
import Settings from "./pages/Settings";
import * as E from "./pages/Employer";
import * as C from "./pages/Client";
import Admin from "./pages/Admin";
import PublicProfile from "./pages/PublicProfile";
import CareerAI from "./pages/CareerAI";

function AuthCallback() {
  const done = useRef(false);
  const nav = useNavigate();
  const { setUser } = useAuth();
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then(({ data }) => { setUser(data.user); window.history.replaceState(null, "", window.location.pathname); nav(data.user.onboarded ? "/dashboard" : "/onboarding", { replace: true }); })
      .catch((e) => { setUser(false); nav(`/signin?error=${encodeURIComponent(errMsg(e))}`, { replace: true }); });
  }, [nav, setUser]);
  return <div className="container-x"><Loading text="Signing you in with Google…" /></div>;
}

function Shell() {
  const loc = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const h1 = document.querySelector("main h1");
    document.title = h1 ? `${h1.textContent} | OpenPath` : "OpenPath";
  }, [loc.pathname]);
  return (
    <>
      <a href="#main" className="skip-link" data-testid="skip-to-main">Skip to main content</a>
      <Header />
      <main id="main" tabIndex={-1} className="min-h-[60vh] outline-none"><Outlet /></main>
      <Footer />
      <A11yPanel />
    </>
  );
}

const P = (el, roles) => <RequireAuth roles={roles}>{el}</RequireAuth>;

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<S.About />} />
        <Route path="/accessibility" element={<S.AccessibilityPage />} />
        <Route path="/employers" element={<S.ForEmployers />} />
        <Route path="/professionals" element={<S.ForProfessionals />} />
        <Route path="/help" element={<S.Help />} />
        <Route path="/contact" element={<S.Contact />} />
        <Route path="/privacy" element={<S.Privacy />} />
        <Route path="/terms" element={<S.Terms />} />
        <Route path="/guidelines" element={<S.Guidelines />} />
        <Route path="/signin" element={<A.SignIn />} />
        <Route path="/signup" element={<A.SignUp />} />
        <Route path="/forgot-password" element={<A.ForgotPassword />} />
        <Route path="/reset-password" element={<A.ResetPassword />} />
        <Route path="/verify-email" element={<A.VerifyEmail />} />
        <Route path="/onboarding" element={P(<Onboarding />)} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/jobs/:id" element={<JobDetail />} />
        <Route path="/freelancing" element={<FreelanceList />} />
        <Route path="/freelancing/:id" element={<ProjectDetail />} />
        <Route path="/government" element={<GovList />} />
        <Route path="/government/:id" element={<GovDetail />} />
        <Route path="/community" element={<CommunityFeed />} />
        <Route path="/community/posts/:id" element={<PostDetail />} />
        <Route path="/community/directory" element={<Directory />} />
        <Route path="/community/professionals/:id" element={<ProfessionalDetail />} />
        <Route path="/u/:id" element={<PublicProfile />} />
        <Route path="/dashboard" element={P(<Dashboard />)} />
        <Route path="/career-ai" element={P(<CareerAI />)} />
        <Route path="/dashboard/applications" element={P(<M.MyApplications />)} />
        <Route path="/dashboard/proposals" element={P(<M.MyProposals />)} />
        <Route path="/dashboard/saved" element={P(<M.Saved />)} />
        <Route path="/dashboard/mentorship" element={P(<M.Mentorship />)} />
        <Route path="/notifications" element={P(<Notifications />)} />
        <Route path="/settings" element={P(<Settings />)} />
        <Route path="/employer" element={P(<E.EmployerHome />, ["employer", "admin"])} />
        <Route path="/employer/jobs/new" element={P(<E.JobForm />, ["employer", "admin"])} />
        <Route path="/employer/jobs/:id/edit" element={P(<E.JobForm />, ["employer", "admin"])} />
        <Route path="/employer/jobs/:id/applicants" element={P(<E.Applicants />, ["employer", "admin"])} />
        <Route path="/client" element={P(<C.ClientHome />, ["client", "admin"])} />
        <Route path="/client/projects/new" element={P(<C.ProjectForm />, ["client", "admin"])} />
        <Route path="/client/projects/:id/edit" element={P(<C.ProjectForm />, ["client", "admin"])} />
        <Route path="/client/projects/:id/proposals" element={P(<C.Proposals />, ["client", "admin"])} />
        <Route path="/admin" element={P(<Admin />, ["admin"])} />
        <Route path="*" element={<S.NotFound />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <A11yProvider>
          <AppRouter />
          <Toaster position="top-center" richColors closeButton />
        </A11yProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
